import {
  useDeleteAssessmentMutation,
  useUpdateAssessmentMutation,
} from '@/app/redux/endpoints/assessments';
import { useUpdateStudentMutation } from '@/app/redux/endpoints/students';
import { useAppDispatch } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import {
  openConfirmationDialogue,
  setAttemptedProFeature,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import { Assessment, AssessmentStudent } from '@/common/types/assessment';
import {
  PlacementTest,
  PlacementTestResult,
} from '@/common/types/placementTest';
import { ControllerType } from '@/common/types/student';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import { ProIcon } from '@/common/ui/UpgradeButton';
import AssessmentsTableRow from '@/dashboard/Assessment/AssessmentsTableRow';
import PlacementTestRow from '@/dashboard/Assessment/PlacementTestRow';
import PlacementTestDialogue from '@/dashboard/LearnerView/ActivityFeed/PlacementTestDialogue';
import { PencilLineIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AssessmentsTableProps {
  assessments: Assessment[];
  placementTests: PlacementTest[];
  onCreateAssessment: () => void;
  visibleStudentIds?: string[];
}
export default function AssessmentsTable({
  assessments,
  placementTests,
  onCreateAssessment,
  visibleStudentIds,
}: AssessmentsTableProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { studentsById } = useStudents();
  const [updateAssessment] = useUpdateAssessmentMutation();
  const [deleteAssessment] = useDeleteAssessmentMutation();
  const [updateStudent] = useUpdateStudentMutation();

  const [selectedPlacementTestResult, setSelectedPlacementTestResult] =
    useState<PlacementTestResult | null>(null);

  const sortedAssessments = useMemo(() => {
    return [...assessments].sort((a, b) => b.createdAt - a.createdAt);
  }, [assessments]);
  const sortedPlacementTests = useMemo(() => {
    return [...placementTests]
      .sort((a, b) => b.createdAt - a.createdAt)
      .filter((a) => !!studentsById.get(a.studentId));
  }, [placementTests, studentsById]);

  const totalItems = useMemo(
    () => sortedAssessments.length + sortedPlacementTests.length,
    [sortedAssessments, sortedPlacementTests],
  );

  function handlePhaseChange(id: string, phase: number) {
    const assessment = assessments.find((a) => a.id === id);
    if (!assessment || !currentOrganisationId) return;
    updateAssessment({
      orgId: currentOrganisationId,
      assessmentId: id,
      updates: {
        phase,
        students: Object.entries(assessment.students).reduce(
          (acc, [id, s]) => {
            acc[id] = { ...s, phase };
            return acc;
          },
          {} as Record<string, AssessmentStudent>,
        ),
      },
    });
  }

  function handleControlTypeChange(id: string, control: ControllerType) {
    const assessment = assessments.find((a) => a.id === id);
    if (!assessment || !currentOrganisationId) return;
    updateAssessment({
      orgId: currentOrganisationId,
      assessmentId: id,
      updates: {
        control,
        students: Object.entries(assessment.students).reduce(
          (acc, [id, s]) => {
            acc[id] = { ...s, control };
            return acc;
          },
          {} as Record<string, AssessmentStudent>,
        ),
      },
    });
  }

  function handlePlacementChange(id: string, placeAtAssessedLevel: boolean) {
    const assessment = assessments.find((a) => a.id === id);
    if (!assessment || !currentOrganisationId) return;
    updateAssessment({
      orgId: currentOrganisationId,
      assessmentId: id,
      updates: {
        placeAtAssessedLevel,
        students: Object.entries(assessment.students).reduce(
          (acc, [id, s]) => {
            acc[id] = { ...s, placeAtAssessedLevel };
            return acc;
          },
          {} as Record<string, AssessmentStudent>,
        ),
      },
    });
  }

  async function handleDeleteAssessment(assessment: Assessment) {
    if (!currentOrganisationId) return;
    const studentIds =
      assessment.studentIds ?? Object.keys(assessment.students);
    await deleteAssessment({
      orgId: currentOrganisationId,
      assessmentId: assessment.id,
    }).unwrap();
    for (const studentId of studentIds) {
      await updateStudent({
        orgId: currentOrganisationId,
        studentId,
        updates: { assessmentId: null },
      });
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-muted/50">
          <TableHead className="pl-4">Created</TableHead>
          <TableHead className="pl-4">Not Started</TableHead>
          <TableHead className="pl-4">Started</TableHead>
          <TableHead className="pl-4">Completed</TableHead>
          <TableHead className="pl-4">Content</TableHead>
          <TableHead className="pl-4">Control Type</TableHead>
          <TableHead className="pl-4">Placement</TableHead>
          <TableHead className="pl-4">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {totalItems > 0 ? (
          <>
            {sortedAssessments.map((assessment) => (
              <AssessmentsTableRow
                key={assessment.id}
                assessment={assessment}
                visibleStudentIds={visibleStudentIds}
                onSelect={() =>
                  navigate(`/dashboard/assessments/${assessment.id}`)
                }
                handlePhaseChange={(phase) =>
                  handlePhaseChange(assessment.id, phase)
                }
                handleControlTypeChange={(control) =>
                  handleControlTypeChange(assessment.id, control)
                }
                handlePlacementChange={(placeAtAssessedLevel) =>
                  handlePlacementChange(assessment.id, placeAtAssessedLevel)
                }
                handleDeleteAssessment={() => {
                  setConfirmationDialogueCallback(async () => {
                    await handleDeleteAssessment(assessment);
                  });
                  dispatch(
                    openConfirmationDialogue({
                      title: 'Delete Assessment',
                      description: [
                        'Are you sure you want to delete this assessment? This cannot be undone.',
                        'If any learners have already completed this assessment, their results will still be viewable from their activity feed.',
                      ],
                      variant: 'destructive',
                    }),
                  );
                }}
              />
            ))}
            {sortedPlacementTests.map((placementTest) => (
              <PlacementTestRow
                key={placementTest.id}
                placementTest={placementTest}
                onViewResult={(result) =>
                  setSelectedPlacementTestResult(result)
                }
              />
            ))}
          </>
        ) : (
          <TableRow className="hover:bg-white">
            <TableCell colSpan={8}>
              <div
                className={`w-full min-h-[${
                  placementTests.length > 0 ? '50%' : '70vh'
                }] flex flex-col items-center justify-center py-12 gap-6`}
              >
                <PencilLineIcon
                  size={80}
                  className="text-neutral-400"
                  weight="thin"
                />
                <div className="flex flex-col items-center gap-2">
                  <h3 className="text-lg font-medium text-neutral-900">
                    No assessments yet
                  </h3>
                  <p className="text-sm text-neutral-500 max-w-[400px] text-center">
                    Create an assessment to track your learners' progress and
                    understanding over time.
                  </p>
                </div>
                <Button
                  onClick={() =>
                    isFeatureEnabled('assessments')
                      ? onCreateAssessment()
                      : dispatch(setAttemptedProFeature('Assessments'))
                  }
                  className="gap-2"
                >
                  <PlusIcon size={20} />
                  Create Assessment
                  {!isFeatureEnabled('assessments') && <ProIcon />}
                </Button>
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      <PlacementTestDialogue
        result={selectedPlacementTestResult}
        onClose={() => setSelectedPlacementTestResult(null)}
      />
    </Table>
  );
}
